/** 로그인 계약. PRD `S1`(네이버 + Apple 소셜 로그인)과 `13.1` O-7 확정을 옮겨 적는다.
 *
 * **O-7 확정: API 는 Bearer 토큰이 정본이고, 웹은 그 토큰을 HttpOnly 쿠키로 감싼다.**
 * 서버의 검증 경로는 하나다. 앱은 토큰을 그대로 헤더에 싣고, 웹은 쿠키에 담아 보낸다.
 * 이 파일이 상수를 두는 이유가 그것이다. 헤더 이름과 쿠키 이름이 앱·웹·서버에 각각
 * 문자열로 흩어지면, 한 자리를 고쳤을 때 나머지가 조용히 옛 이름을 계속 쓴다.
 *
 * **엔드포인트 경로를 여기에 적지 않는다.** `api.ts` 의 규약("실제로 구현된 것만 적는다.
 * 여기 있으면 클라이언트가 그것을 부른다")이 이 파일에도 그대로 걸린다. **2026-09-09 에
 * 로그인 라우트가 실제로 생겼고, 경로는 그 규약대로 `api.ts` 의 `authEndpoints` 에
 * 붙였다.** 이 파일은 여전히 경로를 들고 있지 않다.
 *
 * 스키마 쪽 짝은 `apps/api/migrations/0005_auth_session_consent_history.sql` 이고
 * 근거는 `docs/DATA_DICTIONARY.md` 10장이다. **0005 는 2026-09-10 에 원격 `makit-db` 와
 * 로컬 D1 양쪽에 적용됐다**(같은 문서 10.8). 앱 복귀 경로(O-39)의 일곱 칸도 그 안에 있다.
 *
 * ⚠️ **적용된 뒤로 0005 파일은 고치지 않는다.** 로그인 스키마를 더 바꿔야 하면 새
 * 마이그레이션으로 뺀다. 실제로 `0006_identity_provider_kakao.sql` 이 그렇게 나왔다
 * (`IdentityProvider` 에 `KAKAO` 를 더하면서 `provider` 의 CHECK 를 넓힌 회차다.
 * 근거는 `docs/DATA_DICTIONARY.md` 11장).
 *
 * **그 0006 도 같은 날 양쪽에 적용됐다**(로컬·원격. 같은 문서 2장의 실측 표). 그래서
 * **이제 0006 도 고치지 않는다. 다음 변경은 0007 이다.** 규율이 한 파일 뒤로 옮겨 갔을
 * 뿐이고, 이유는 같다. 이름은 같고 내용이 다른 것을 `d1_migrations` 는 알려 주지 않는다.
 */

import type { ConsentDocType, IdentityProvider } from "./domain";

// ── 토큰을 싣는 자리 ─────────────────────────────────────────────────────────

/** 앱이 쓰는 자리. `Authorization: Bearer <token>`. */
export const AUTHORIZATION_HEADER = "Authorization";

/** 위 헤더의 접두사. 대소문자를 포함해 이 문자열 그대로다. */
export const BEARER_PREFIX = "Bearer ";

/** 웹이 쓰는 자리. **같은 토큰을 담는다.** 서버는 이 쿠키에서 꺼낸 값과 헤더에서 꺼낸 값을
 *  구분하지 않고 같은 검증에 넘긴다. 그것이 O-7 의 요점이다.
 *
 * 쿠키 속성(HttpOnly · Secure · SameSite)은 서버가 붙이는 것이라 여기 없다. **다만 쿠키로
 * 온 요청에는 CSRF 축이 하나 더 붙는다**. 그 판단은 "쿠키에서 꺼냈는가"라는 요청 시점의
 * 사실로 하고, 세션 행에는 그 값을 저장하지 않는다. 저장하면 검증 코드가 그 값으로
 * 분기하고 싶어지고, 그 순간 검증 경로가 둘이 된다. */
export const SESSION_COOKIE_NAME = "makit_session";

/** `Authorization` 헤더에서 토큰을 꺼낸다. 접두사를 손으로 자르는 코드가 여러 자리에
 *  생기지 않게 한 곳에 둔다.
 *
 * **쿠키에서 꺼내는 짝은 여기 없다.** 쿠키 파싱은 서버에서만 일어나고, 이 파일은 앱과
 * 웹에서도 도는 순수 TypeScript 자리다(`index.ts` 머리말). 서버는 헤더를 먼저 보고
 * 없으면 쿠키를 본다. 둘 다 있는 요청에서 사람이 의도한 것은 명시적으로 붙인 헤더 쪽이다. */
export function readBearerToken(headerValue: string | null | undefined): string | null {
  if (!headerValue) return null;
  if (!headerValue.startsWith(BEARER_PREFIX)) return null;
  const token = headerValue.slice(BEARER_PREFIX.length).trim();
  return token.length > 0 ? token : null;
}

// ── 공급자 ───────────────────────────────────────────────────────────────────

/** `IdentityProvider` 의 런타임 목록. `search.ts` 의 `JOB_STATUS_PRESENCE` 와 같은 수법이다.
 *
 * 아래 `Record` 가 빠짐을 컴파일에서 막는다. `domain.ts` 에 공급자를 더하면 키가 모자라
 * `pnpm typecheck` 가 멈춘다. **라우트가 자기 목록을 손으로 들고 있지 않게 하려는 것이다** .
 * `routes/jobs.ts` 의 `KNOWN_JOB_STATUSES` 가 그렇게 두 번째 정본이 됐다. */
const IDENTITY_PROVIDER_PRESENCE: Record<IdentityProvider, true> = {
  NAVER: true,
  APPLE: true,
  KAKAO: true,
};

export const ALL_IDENTITY_PROVIDERS = Object.keys(IDENTITY_PROVIDER_PRESENCE) as IdentityProvider[];

export function isIdentityProvider(value: string): value is IdentityProvider {
  return Object.prototype.hasOwnProperty.call(IDENTITY_PROVIDER_PRESENCE, value);
}

/** **앱과 웹 양쪽에서 켠 공급자.** 앱(`apps/mobile`)은 **이 목록만** 읽는다. 웹과 서버의 웹
 *  시작 요청은 아래 `WEB_ENABLED_IDENTITY_PROVIDERS` 를 읽는다. 서버는 `source=APP` 시작
 *  요청이면 이 목록에 없는 공급자를 404 로 막는다(앱 경로에서 Apple 이 열리지 않게).
 *  타입과 DB CHECK 에는 셋 다 들어 있어 **공급자를 붙일 때 마이그레이션이 필요 없다**
 *  (카카오는 0006 이 그 자리를 만들었다).
 *
 *  Apple 은 앱(iOS·Android)과 웹 양쪽에 둔다(2026-10-05 사용자 확정, `docs/PRD.md`
 *  2026-10-08 개정). **지금은 이 목록과 아래 웹 목록 둘 다에서 꺼 둔다.** 켜는 시점은
 *  아래 웹 목록 주석에 적었다. 앱은 네이티브 SDK 를 쓰지 않고 웹과 같은 Services ID 의
 *  시스템 브라우저 경로로 들어와, 기존 일회용 코드와 PKCE 로 앱에 돌아온다. 그래서 `aud` 로
 *  인정하는 값은 Services ID 하나다(PRD `13.1` O-54). 이 앱 경로는 실기기 확인 대상이다.
 *
 * 이 목록과 `ALL_IDENTITY_PROVIDERS` 가 다른 것이 맞다. 앞은 "지금 로그인 화면에 뜨는 것",
 * 뒤는 "서버가 값으로 받아들이는 것"이다. `JOB_ORIGIN_FILTERS` 와 `ALL_JOB_ORIGINS` 가
 * 다른 것과 같은 관계다.
 *
 * **`KAKAO` 가 2026-09-10 에 들어왔고, 그 순서가 이 주석이 정한 그대로였다.** 이 배열은
 * 라우트를 여는 스위치라 **구현이 선 뒤에 켠다.** 먼저 켜면 버튼이 눌리고
 * `OAuthNotConfiguredError` 로 떨어진다. 실제 순서는 ① 타입(`domain.ts`)과 스키마(0006),
 * ② `lib/oauth.ts` 의 `KakaoOAuthProvider` 와 `makeProvider` 의 갈래, ③ **이 줄**이었다.
 *
 * ⚠️ **이 배열에 이름이 있다고 그 공급자로 로그인이 끝까지 되는 것은 아니다.** 이것이
 * 여는 것은 **우리 라우트**이고, 그다음은 공급자 콘솔의 설정(로그인 활성화 · Redirect URI
 * 등록 · 동의항목)이라 코드가 알 수 없다. 그쪽이 안 되어 있으면 셀러는 우리 화면이 아니라
 * **공급자 화면에서** 막힌다. 즉 **이 배열은 "우리 쪽 준비가 끝났다"까지만 말한다.** */
// ⚠️ **카카오를 잠시 껐다 (2026-09-10).** 카카오 개발자 계정이 "비정상적인 로그인 시도"로
// 정지됐다. 이 목록에 KAKAO 가 있으면 로그인 화면에 버튼이 살아 있고, 누를 때마다 콘솔에서
// 꺼져 있는 앱으로 인가 요청이 나가 실패 시도가 더 쌓인다. **계정이 풀리고 콘솔에서 카카오
// 로그인을 켠 뒤 이 줄을 되돌린다.** 구현·스키마·문서는 그대로 두었으므로 되돌리는 것은
// 이 한 줄이다. 목업(A-01·W-01)과 PRD S1 의 확정은 카카오를 포함하며 바뀌지 않았다.
export const ENABLED_IDENTITY_PROVIDERS: IdentityProvider[] = ["NAVER", "KAKAO"];

/** **웹에서 켠 공급자.** 웹 로그인 화면(`apps/web` `Login.tsx`)과 서버 라우트(웹 시작 요청 ·
 *  콜백)가 읽는다. 앱은 이것을 읽지 않는다. **지금은 위 목록과 값이 같다.**
 *
 * ⛔ **Apple 은 구현만 들어와 있고 꺼져 있다 (2026-10-03).** 서버 구현
 * (`apps/api/src/lib/apple-oauth.ts` · `makeProvider` 의 Apple 갈래 · 폼 콜백)은 있지만 **이 목록에
 * `APPLE` 이 없어서 서버의 `/api/auth/apple/start` · 콜백이 404 이고 웹 버튼도 꺼져 있다.
 * 배포본에 Apple 시크릿(Team ID · Key ID · `.p8` 등)이 들어가도 켜지지 않는다.** 꺼 두는 근거를
 * "배포본에 시크릿이 없다"는 저장소 밖 사실이 아니라 이 줄로 둔 것이다.
 *
 * **켜는 것은 별도 PR 이다.** 탈퇴 때 Apple 연결을 어떻게 끊을지(`docs/PRD.md` `13.1` O-55)가
 * 정해지고, `docs/LEGAL_IP_GUIDE.md` `G1-30`("Apple 을 켜기 전에 닫을 것")과 웹 탈퇴 경로(`L-C32`)가
 * 닫힌 뒤에 두 목록에 `"APPLE"` 을 더한다. `docs/PRD.md` S1 · `G1-30` 이 "이 배열이 바뀌는 커밋에서
 * `G1-30` 을 다시 읽는다"고 적어 두었다. 게이트 판정은 `legal-ip-agent` 소관이다. Apple 의
 * refresh/access token 은 저장하지 않으므로 `G1-30`-⑤(탈퇴 시 Apple 토큰 폐기)는 그 PR 전에
 * 방법이 정해져야 한다.
 *
 * ⛔ **이 웹 목록에만 먼저 `"APPLE"` 을 넣지 않는다**(PRD 2026-10-08 개정). 웹 탈퇴 경로(`L-C32`)가
 * 서기 전에 웹에서만 켜면, 웹에서 Apple 로 가입한 셀러가 스스로 탈퇴하거나 학습 이용 동의를 철회할
 * 수단이 없다.
 *
 * **앱 목록과 따로 둔 이유**: 두 목록은 출처(앱 · 웹)별로 라우트를 여는 스위치다. 출처마다 준비와
 * 검증(앱은 실기기 확인)이 끝나는 때가 다를 수 있어 한 줄로 묶지 않았다. 앱 · 웹을 함께 켜는 날에는
 * 두 목록을 함께 바꾼다.
 *
 * 펼침(`...ENABLED_IDENTITY_PROVIDERS`)이 아니라 글자로 적은 것은 일부러다. 켜는 PR 의 diff 가
 * 이 줄에 `"APPLE"` 한 낱말로 드러나게 하려는 것이다. 위 목록에 공급자를 더하면 여기에도 같이
 * 더한다. */
export const WEB_ENABLED_IDENTITY_PROVIDERS: IdentityProvider[] = ["NAVER", "KAKAO"];

/** 공급자를 **셀러 화면에 적을 때 쓰는 낱말.**
 *
 * `NAVER` 라는 값이 화면에 나가지 않게 하는 자리다. 낱말의 정본은 이 파일이 아니라
 * `docs/PRD.md` `7.5.9` 용어 표이고, 그 표가 **"네이버·Apple을 가리켜야 할 때는 개별 이름
 * 그대로(`네이버` · `Apple`)"** 라고 못박았다. 여기 있는 두 값은 그 줄을 옮겨 적은 것이다.
 * **표에 없는 낱말을 이 자리에서 새로 짓지 않는다** — 그러려면 표부터 고쳐야 하고 그것은
 * `design-manager` 소관이다.
 *
 * ⚠️ 같은 표가 **`제공자`(provider) 를 금지어로 둔다.** 이 제품에는 배경을 지우는 외부
 * API 도 `provider` 라서, 한 낱말이 "네이버 계정"과 "배경 지우기 업체"를 함께 가리키게
 * 된다. 그래서 상수 이름은 구현 용어여도 되지만 **값은 언제나 개별 이름**이다.
 *
 * `Record<IdentityProvider, string>` 이라 공급자를 늘리면서 낱말을 빠뜨리면 컴파일에서
 * 막힌다. 그때 채울 낱말은 용어 표에서 가져온다.
 *
 * ⚠️ **`카카오` 는 표의 줄이 아니라 규칙에서 나왔다.** 표의 그 줄은 아직 `네이버·Apple을
 * 가리켜야 할 때` 라고만 적혀 있고, 그 줄이 정한 규칙이 **개별 이름 그대로**다. 정본 목업
 * `A-01`·`W-01` 의 버튼도 `카카오로 시작하기` 이므로 두 근거가 같은 낱말을 가리킨다.
 * **표의 줄 자체를 셋으로 넓히는 것은 `design-manager` 소관이다.** 여기서 낱말을 새로 짓지
 * 않았다는 뜻이지, 표를 고칠 필요가 없다는 뜻이 아니다. */
export const IDENTITY_PROVIDER_LABEL: Record<IdentityProvider, string> = {
  NAVER: "네이버",
  APPLE: "Apple",
  KAKAO: "카카오",
};

// ── `providerUserId` 에 걸리는 규약 (값이 아니라 규칙이라 상수를 만들지 않는다) ──────
//
// 1. **불투명 문자열이다.** 형식·길이·문자 집합을 가정하지 않는다. 숫자로 보여도 숫자로
//    다루지 않는다. `SellerIdentity.providerUserId` 에 길이 CHECK 가 없는 이유다.
// 2. **재사용되지 않는다.** 같은 값이 다른 사람에게 다시 배정되지 않는다는 전제 위에
//    `UQ(provider, providerUserId)` 가 서 있다.
// 3. **이메일이 아니다.** 이메일은 바뀌고 Apple 릴레이는 특히 그렇다. 식별에 쓰지 않는다.
// 4. **화면에 내보내지 않는다.** 셀러에게도 로그에도 원문을 적지 않는다.
// 5. ⚠️ **우리 애플리케이션(클라이언트 ID)에 종속일 수 있다.** 공급자 애플리케이션을 새로
//    발급하면 같은 사람이 다른 식별자로 보일 가능성이 있고, 이 파일을 쓰는 시점에 그것이
//    확인되지 않았다. 그러니 **애플리케이션 교체는 설정 변경이 아니라 계정 이관 작업으로
//    다룬다.**
// 6. **이메일이 같다고 계정을 합치지 않는다.** 네이버로 들어온 사람의 이메일이 기존
//    셀러의 이메일과 같아도 자동 병합하지 않는다. 그 병합은 계정 탈취 경로가 된다.
//    두 번째 공급자를 붙이는 것은 **이미 로그인한 상태에서** 하는 명시적 동작이다.

// ── 약관 동의 ────────────────────────────────────────────────────────────────

/** 지금 받는 동의 판. **여기가 정본이다.**
 *
 * `Consent.docVersion` 은 문자열이고 DB 는 오타를 잡아 주지 않는다. 문자열을 라우트에
 * 직접 적으면 `v1.2` 와 `1.2` 가 섞여 들어오고, 그 순간 "이 셀러가 어느 판에 동의했나"를
 * 세는 질의가 둘로 갈린다. 규격을 D1 이 아니라 코드에 둔 것과 같은 이유다
 * (`docs/DATA_DICTIONARY.md` 5.4).
 *
 * **`null` 은 "그 문서가 아직 없다"는 뜻이고, 값이 `null` 인 동의는 받을 수 없다.**
 * 약관 문서가 확정되면 고칠 자리가 여기 하나다. `REFUND_POLICY` 는 필요 여부 자체가
 * 법무 검토 대상이라(`docs/LEGAL_IP_GUIDE.md`) 값도 자리도 열어만 둔다. */
export const CURRENT_CONSENT_VERSIONS: Record<ConsentDocType, string | null> = {
  TERMS: null,
  PRIVACY: null,
  TRAINING_USE: null,
  REFUND_POLICY: null,
};

/** 로그인 화면에 함께 두는 동의. 배열 순서가 화면 순서다.
 *
 * PRD `S1` 확정: **로그인 화면에 약관 동의를 함께 둔다.** 근거는 `2.2` 의 순서 제약이다.
 *
 * ## ⛔ `TRAINING_USE` 는 여기 없다 (2026-09-24 사용자 확정. PRD `13.1` `O-38`)
 *
 * **학습 이용 동의를 켜는 자리는 마이페이지 하나다**(`A-41`·`W-70`). **가입 흐름에 안
 * 넣는다.** 가입할 때 함께 받으면 그 동의가 **가입의 조건으로 읽힐 여지**가 생기고, 그것이
 * `O-19`(기본 꺼짐 · 켜는 자리 하나 · 권유 없음)가 막으려는 바로 그 모양이다.
 *
 * ⚠️ **`CLAUDE.md` 의 「이용약관의 학습 이용 동의를 1단계부터 넣는다」가 깨진 것이 아니다.**
 * 바뀐 것은 **받는 자리**이지 **1단계부터 있다는 사실**이 아니다. 약관·처리방침에는 1단계부터
 * 그대로 적혀 있고, **켜는 스위치만 마이페이지에 있다.** 그래서 "그 시점부터의 데이터를 쓸 수
 * 있게 해 둔다"는 요구는 문서 쪽에서 그대로 서 있다.
 *
 * ⛔ **되돌려 넣지 마라.** 지금 `CURRENT_CONSENT_VERSIONS.TRAINING_USE` 가 `null` 이라
 * 무해해 보이지만, **그 판에 값이 들어가는 커밋에서 가입 흐름이 학습 이용 동의를 묻기
 * 시작한다**(게이트 `G1-19`-⑫). 시한은 배포가 아니라 그 커밋이다. */
export const SIGNUP_CONSENT_DOC_TYPES: ConsentDocType[] = ["TERMS", "PRIVACY"];

/** 위 중에서 **동의하지 않으면 가입이 성립하지 않는 것.**
 *
 * ⚠️ **지금 위 배열과 값이 같지만 같은 것이 아니다.** 위는 **화면에 무엇을 두는가**이고
 * 여기는 **무엇이 없으면 가입이 안 되는가**다. 둘을 한 상수로 합치면, 가입을 막지 않는
 * 동의를 화면에 하나 더 두는 날 그 낱말이 곧바로 가입 조건이 된다.
 *
 * `TRAINING_USE` 가 여기 없다. **2026-09-24 부터는 위 배열에도 없다**(`O-38`) — 가입
 * 흐름에서 아예 묻지 않으므로 가입 조건이 될 수 있는지를 물을 자리 자체가 없다.
 * ⛔ **그 미결(`docs/LEGAL_IP_GUIDE.md` `L-B4`)이 답해져서 빠진 것이 아니다.** 묻는
 * 자리가 마이페이지로 옮겨졌을 뿐이고, `SellerSetting.trainingUseOptIn` 이 `NULL` 을
 * 허용하는 것도 그대로다. */
export const BLOCKING_CONSENT_DOC_TYPES: ConsentDocType[] = ["TERMS", "PRIVACY"];

/** 가입·재동의 때 클라이언트가 보내는 한 줄.
 *
 * **판을 클라이언트가 보낸다.** 서버가 `CURRENT_CONSENT_VERSIONS` 로 덮어쓰지 않고
 * 대조한다. 셀러가 본 화면의 판과 서버가 저장하는 판이 다르면 동의 기록이 거짓이 된다. */
export interface ConsentSubmission {
  docType: ConsentDocType;
  /** 셀러가 실제로 본 판. 서버가 현재 판과 다르면 거절한다. */
  docVersion: string;
  /** 체크했는가. `false` 도 보낸다. 안 보내는 것과 거절한 것은 다른 사실이다. */
  agreed: boolean;
}

// ── 로그인 상태 ──────────────────────────────────────────────────────────────

/** 앱·웹이 켜질 때 한 번 묻는 "나 누구인가". `MyPageSummaryView` 와 다른 것이다.
 *  그쪽은 마이페이지 화면의 집계이고 이쪽은 **화면을 그릴지 로그인으로 보낼지**를 가른다.
 *
 * `authenticated` 가 `false` 면 나머지는 전부 비어 있다. 토큰이 없거나 만료됐거나
 * 무효화됐거나 셋 중 하나이고, **셋을 구분해 내려보내지 않는다.** 구분해서 알려 주면
 * 토큰을 가진 쪽에게 "이 토큰은 존재했으나 죽었다"를 알려 주는 셈이 된다. */
export interface SessionView {
  authenticated: boolean;
  /** 마이페이지 헤더에 적는 이름. 공급자가 주는 값이고 없으면 `null` 이다. */
  displayName: string | null;
  /** 이번 세션이 어느 수단으로 들어왔는가. */
  provider: IdentityProvider | null;
  /** **같은 셀러인가를 가르는 불투명한 값.** 미로그인이면 `null` 이다(2026-10-04,
   *  `docs/DATA_DICTIONARY.md` 42장).
   *
   * 값은 `Seller.id` 가 아니라 그 파생값이다. 소문자 16진 32자이고, 만드는 곳은 서버
   * `apps/api/src/lib/auth.ts` 의 `deriveSellerKey` 하나뿐이다
   * (`SHA-256("makit.sellerKey.v1:" + Seller.id)` 의 앞 16바이트). **같은 셀러면 언제나
   * 같고, 이 값에서 `Seller.id` 로 되돌아갈 수 없다.**
   *
   * 원시 `Seller.id` 를 싣지 않은 이유: 웹은 이 값을 `sessionStorage` 에 남긴다. 거기는 브라우저
   * 확장·공유 PC 가 읽을 수 있는 자리이고, `Seller.id` 는 D1 행 키라 로그·지원 문의·다른
   * 응답과 맞춰 보면 **계정을 특정하는 열쇠**가 된다. 이 값은 그 어디와도 맞춰지지 않는다.
   *
   * 서버 시크릿을 섞지 않은 이유: 시크릿을 돌리면 모든 셀러의 값이 한꺼번에 바뀌어 열린 탭이
   * 전부 「다른 셀러」로 판정된다. 이 값의 쓸모는 **비교**이지 위조 방지가 아니다. 고쳐 써도
   * 서버가 내주는 권한은 쿠키·토큰이 정하고 이 값은 아무 문도 열지 않는다. `Seller.id` 가
   * `crypto.randomUUID()` 라 시크릿 없이도 대입으로 거꾸로 찾을 수 없다.
   *
   * ⛔ **규약 넷.**
   * 1. **비교에만 쓴다.** 두 값이 같은가 말고는 아무 뜻도 읽지 않는다. 서버에 되보내지 않는다
   *    (서버도 받지 않는다).
   * 2. **화면에 표시하지 않는다.** 화면이 셀러를 가리킬 때는 `displayName` 이다.
   * 3. **로그·주소(쿼리스트링·해시)·외부 서비스(분석·오류 수집)에 싣지 않는다.** 불투명해도
   *    한 셀러를 끝까지 따라가는 꼬리표라, 바깥에 남으면 그 자체로 추적 식별자가 된다.
   * 4. **클라이언트가 남길 곳은 웹 `sessionStorage` 하나다**(이 값 하나만). `localStorage` 나
   *    쿠키에 두지 않는다. 탭을 닫아도 남기 때문이다.
   *
   * **필수 필드다.** 선택(`?`)으로 두면 서버가 빠뜨려도 컴파일이 통과하고, 웹은 `undefined`
   * 를 받아 늘 「증명 안 됨」으로 판정한다. 그 실패는 화면에 아무 표시도 남기지 않는다.
   *
   * 이름이 `sessionKey` 가 아닌 이유: 이 값은 세션마다 다르지 않다. 같은 셀러가 다시 로그인해도
   * 같다. `session` 이 붙으면 세션 토큰과 헷갈려 규약 3 이 느슨하게 읽힌다. */
  sellerKey: string | null;
  /** **아직 받지 못한 동의.** 판이 올라가면 여기 값이 생기고 화면은 재동의를 받는다.
   *  비어 있으면 밀린 것이 없다.
   *
   * `readonly` 인 이유는 아래 `ANONYMOUS_SESSION` 이 얼어 있기 때문이다. 배열을 얼려 놓고
   * 타입만 변경 가능하다고 적으면, `push` 가 컴파일을 통과하고 런타임에 던진다. */
  pendingConsents: readonly ConsentDocType[];
}

/** 로그인하지 않은 상태의 정본 값. 화면이 `null` 을 각자 해석하지 않게 한다.
 *
 * ⚠️ **얼려 둔다.** 이 객체는 **공유 인스턴스**다. 서버가 미인증 응답으로 이것을 그대로
 * 내보내고(`c.json(ANONYMOUS_SESSION)`), 웹이 조회 실패의 대체값으로 이것을 그대로 쥔다
 * (`query.data ?? ANONYMOUS_SESSION`). **어느 한 자리가 한 번 변형하면 그 뒤의 모든 요청과
 * 모든 화면이 오염된 값을 본다.** 미인증 상태가 갑자기 `authenticated: true` 가 되는
 * 사고는 재현이 거의 불가능하다.
 *
 * **속 배열까지 얼린다.** 겉만 얼리면 `pendingConsents.push(...)` 가 그대로 통해서, 이
 * 방어가 가장 새기 쉬운 자리 하나를 비워 두게 된다. */
export const ANONYMOUS_SESSION: SessionView = Object.freeze({
  authenticated: false,
  displayName: null,
  provider: null,
  sellerKey: null,
  pendingConsents: Object.freeze([] as ConsentDocType[]),
} as const satisfies SessionView);

// ── 내 계정 (`A-48` · `W-70`) ────────────────────────────────────────────────

/** `GET /api/auth/account` 의 응답. **공급자에게서 받은 정보는 이름과 이메일 두 줄뿐이다.**
 * 거기에 우리 서버가 만든 가입 시각 하나가 붙는다(`joinedAt`, 2026-10-31).
 *
 * **공급자 정보가 두 줄인 것 자체가 계약이다.** `docs/DATA_DICTIONARY.md` 10.4 가 "새 컬럼을
 * 하나도 만들지 않는다"로 닫았고, 화면은 **프로필 사진·성별·생일·연령대·휴대전화번호를 받지
 * 않는다**고 셀러에게 적는다. **공급자에게서 받는 칸**이 이 타입에 하나 늘어나는 것은 화면
 * 문구와 네아로 신청서를 함께 고쳐야 하는 일이다(`NAVER_API_CATALOG` 9.4 의 대표 거절 사유가
 * "신청한 제공 정보와 실제 받는 정보의 불일치"다). 이 경고는 `displayName`·`email` 곁에 무엇이
 * 붙든 그대로 살아 있다.
 *
 * ⚠️ **새 칸이 이 경고에 걸리는지는 출처로 가른다.** 값이 공급자 응답에서 오면 걸리고(신청서·
 * 화면 문구·10.4 표를 함께 고친다), 우리 서버가 스스로 만든 값이면 안 걸린다. `joinedAt` 은
 * 뒤쪽이다. 아래 칸 주석과 10.4 「`joinedAt`」 문단이 근거다. 출처를 가리지 않고 "서버 값이니
 * 괜찮다"로 칸을 늘리지 않는다. 공급자 응답을 서버가 옮겨 적은 값(`Seller.displayName` 이
 * 그렇다)은 **서버 컬럼이어도 공급자 값이다.**
 *
 * `SessionView` 와 가르는 기준: 이쪽은 **`내 계정` 화면이 그리는 값**이고 그쪽은
 * **화면을 그릴지 로그인으로 보낼지 가르는 값**이다. `SessionView.displayName` 과 겹치는
 * 것은 헤더가 이름 한 줄을 먼저 그려야 해서이고, 그 한 줄 때문에 세션 응답에 이메일까지
 * 실으면 **모든 요청 앞단에서 이메일이 오간다.** */
export interface AccountProfile {
  /** 공급자가 준 이름. 없으면 `null` 이다. */
  displayName: string | null;
  /** 연락용. **식별에 쓰지 않는다**(`providerUserId` 규약 3). */
  email: string | null;
  /** 가입 시각. `A-40` 계정 머리글의 `2026-09-01 가입` 줄이 그린다.
   *
   * **출처는 `Seller."createdAt"` 이다**(0002, `DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP`).
   * 처음 로그인한 순간 우리 서버가 `Seller` 행을 만들며 D1 기본값으로 찍은 시각이고, INSERT 가
   * 이 칸을 묶지 않는다(`apps/api/src/lib/auth.ts` 의 처음 온 사람 분기). **공급자가 준 값이
   * 아니라서 네아로 신청 제공 정보(이름·이메일. `NAVER_API_CATALOG` 9.5 의 제공 가능 항목에
   * 가입일은 없다)와 무관하고, 위 「공급자 정보 두 줄」 계약을 늘리지 않는다.** 새 컬럼도 아니다.
   *
   * **와이어 형식은 D1 원문 그대로**, `YYYY-MM-DD HH:MM:SS`(UTC, 오프셋 없음)다. 이 저장소의
   * 다른 `createdAt` 들(`JobView` · `JobVersionView` · 결제 이력)과 같다. 화면은 `format.ts` 의
   * `parseServerDate` 로 읽고 **로컬 날짜로 적는다.** ⛔ 문자열 앞 10자를 잘라 쓰지 않는다. UTC
   * 날짜라 KST 오전 9시 전에 가입한 셀러는 하루 전 날짜가 찍힌다.
   *
   * **`null` 이 아니다.** 컬럼이 NOT NULL 이고, 행이 없으면 이 응답 자체가 나가지 않는다. */
  joinedAt: string;
}

// ── 앱 복귀 (`13.1` O-39. 일회용 교환 코드 + 앱↔우리 서버 PKCE) ──────────────
//
// **웹과 앱이 세션을 받는 시점이 다르다.** 웹은 OAuth 콜백에서 바로 받고(쿠키), 앱은
// 콜백에서 **일회용 코드만** 받아 뒤에 교환한다. 세션 토큰 원문을 커스텀 스킴 딥링크에
// 그대로 실으면 두 축이 열리기 때문이다. ⓐ 주소에 남는 잔존과 ⓑ 스킴 가로채기.
// 일회용 코드는 ⓐ 만 닫는다. 가로챈 쪽이 먼저 교환하면 세션을 얻는다. 그래서 **앱이 만든
// PKCE 바인딩**을 얹는다. 코드를 가로채도 verifier 가 없으면 교환이 성립하지 않는다.
//
// **O-7 의 "검증 경로는 하나"가 깨지지 않는다.** 교환이 끝난 뒤부터는 앱도 웹과 같은
// 세션 토큰 하나를 쓰고 같은 `resolveSession` 을 지난다. 이 절이 더하는 것은 **토큰을
// 앱에 건네는 구간** 하나다.
//
// 스키마 짝은 `AuthLoginAttempt` 의 `app*` 일곱 칸이고 근거는
// `docs/DATA_DICTIONARY.md` 10.9 다.
//
// ⚠️ **엔드포인트 경로는 여기 없다.** 이 파일 머리말의 규약대로 `POST /api/auth/app/exchange`
//    는 `api.ts` 의 `authEndpoints.appExchange` 에 있다(2026-09-09, `feat/mobile-login`).
//    **그 줄과 라우트는 한 몸이다.** 라우트가 브랜치에서 빠지면 그 줄도 함께 빠진다.
//    조건은 그 줄의 주석에 적혀 있다.

/** 앱이 쓰는 PKCE 방식. **이 값 하나만 받는다.**
 *
 * `plain` 을 받지 않고, `AuthLoginAttempt` 에 방식을 저장하는 컬럼도 두지 않았다. 방식을
 * 저장할 수 있으면 `plain` 이 저장될 수 있고, 저장될 수 있는 값은 언젠가 저장된다.
 * 서버는 시작 요청의 `code_challenge_method` 가 이 값과 다르면 거절한다. */
export const PKCE_CODE_CHALLENGE_METHOD = "S256";

export type PkceCodeChallengeMethod = typeof PKCE_CODE_CHALLENGE_METHOD;

/** `verifier` 난수의 바이트 수. 세션 토큰(`newSessionToken`)과 같은 32바이트다. */
export const PKCE_VERIFIER_BYTES = 32;

/** `challenge` 문자열의 길이. `BASE64URL_NO_PAD(SHA256(...))` 는 언제나 43자다.
 *
 * **DB 의 CHECK 와 짝이다**(`AuthLoginAttempt_appCodeChallengeShape_chk`). 서버가 시작
 * 요청에서 이 길이를 먼저 보면, 형식이 틀린 값이 CHECK 위반이 아니라 400 으로 끊긴다.
 * ⚠️ 이 길이가 `S256` 을 증명하지는 **않는다**. 32바이트를 base64url 로 적어도 43자라서,
 * `plain` verifier 가 이 검사를 통과한다. 방식을 가르는 것은 위 상수의 대조다. */
export const PKCE_CODE_CHALLENGE_LENGTH = 43;

/** 앱이 `GET /api/auth/:provider/start` 에 붙이는 질의 이름.
 *
 * **이름을 상수로 두는 이유가 이 파일의 머리말과 같다.** 앱이 `code_challenge` 로 보내고
 * 서버가 `codeChallenge` 로 읽으면, 서버는 값이 없다고 판단하고 **PKCE 없이 로그인을
 * 시작한다.** 그 실패는 화면에 아무것도 남기지 않는다.
 *
 * ⚠️ **PKCE 두 칸만 snake_case 다.** 우리 질의 이름 규약(`returnTo`·`source`)과 다른 것이
 * 맞다. 그 두 이름은 PKCE 규격의 낱말이고, 규격을 따르는 자리에서 이름을 우리 식으로
 * 바꾸면 규격을 읽고 온 사람이 매번 대조표를 찾아야 한다. */
export const APP_LOGIN_START_PARAMS = {
  /** `APP` 이어야 앱 경로가 열린다. 없으면 `WEB` 이다. */
  source: "source",
  codeChallenge: "code_challenge",
  codeChallengeMethod: "code_challenge_method",
  /** 앱이 만든 대조값. **우리와 네이버 사이의 `state` 와 다른 것이다.** */
  appState: "appState",
} as const;

/** 콜백이 앱으로 되돌려보내는 딥링크에 실리는 질의 이름.
 *
 * 성공하면 `code` 와 `appState` 가 실린다. **세션 토큰은 실리지 않는다.** 실패하면 둘 다
 * 없고 실패 사유만 실린다(`api.ts` 의 `LOGIN_REASON_PARAM`. 문구는 화면이 고른다).
 *
 * ⚠️ 앱은 **`appState` 가 자기가 만든 값과 같을 때만** 교환을 시작한다. 그 대조를 앱이
 * 건너뛰면, 남이 만든 코드를 앱이 대신 교환해 주는 경로가 열린다. */
export const APP_LOGIN_RETURN_PARAMS = {
  code: "code",
  appState: "appState",
} as const;

/** `POST /api/auth/app/exchange` 의 본문.
 *
 * ⚠️ **두 값 다 비밀이고 둘 다 로그에 남기지 않는다.** `code` 는 딥링크로 온 원문이고
 * (DB 에는 그 SHA-256 만 있다), `code_verifier` 는 앱만 갖고 있던 난수다. 서버는
 * verifier 를 해싱해 저장된 challenge 와 대조하고 **버린다. 저장하는 자리가 없다.**
 *
 * 필드 이름이 snake_case 인 이유는 위 `APP_LOGIN_START_PARAMS` 와 같다. PKCE 규격의
 * 낱말이라 그대로 쓴다. */
export interface AppExchangeRequest {
  /** 딥링크로 받은 일회용 코드 원문. **한 번만 통한다.** */
  code: string;
  /** 이 로그인 시도를 시작할 때 앱이 만든 `verifier` 원문. */
  code_verifier: string;
}

/** 위 요청의 응답. **줄이 하나뿐인 것이 계약이다.**
 *
 * 세션 토큰 원문이 나가는 자리는 **이 응답 본문 하나**다. 딥링크에도 로그에도 남지 않는다.
 * 앱은 이 값을 안전한 저장소에 넣고 그 뒤 모든 요청에 `Authorization: Bearer` 로 싣는다.
 *
 * **`SessionView` 를 함께 싣지 않는다.** 로그인 직후 화면이 필요한 값은 앱이 켜질 때 이미
 * 부르는 `GET /api/auth/session` 이 준다. 여기 함께 실으면 같은 상태의 출처가 둘이 되고,
 * 두 응답이 어긋났을 때 어느 쪽이 정본인지 말할 근거가 없다. 앱은 토큰을 저장한 뒤 세션
 * 조회를 무효화하면 된다. */
export interface AppExchangeResponse {
  /** ⚠️ 토큰 **원문**이다. `code` 와 헷갈리지 않게 이름을 길게 뒀다. */
  sessionToken: string;
}
